// src/hooks/useTables.js

import { useState, useEffect } from 'react';
import { tableService } from '../services/tableService';
import { TABLES as FALLBACK_TABLES } from '../config/tables';
import { toast } from 'react-hot-toast';

/**
 * useTables Hook
 * Custom hook for fetching and managing tables from backend API
 * Falls back to hardcoded tables if API fails
 */
export const useTables = () => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);

  /**
   * Process table data from API
   * Uses sectionName if available, otherwise uses idSection
   */
 const processTableData = (rawTables) => {
  if (!Array.isArray(rawTables)) return [];

  return rawTables.map(table => {
    // Flatten splits into runningOrders array
    // ✅ Include 'active', 'hold', AND 'payment_pending' splits
    const runningOrders = table.split
      ?.filter(split =>
        (split.status === 'active' || split.status === 'hold' || split.status === 'HOLD' || split.status === 'payment_pending') &&
        split.runningOrders &&
        split.runningOrders.length > 0
      )
      ?.flatMap(split =>
        split.runningOrders.map(run => ({
          tableSplit: split.splitName || null,
          pax: split.peopleCount || 0,
          status: split.status?.toLowerCase() === 'payment_pending' ? 'payment_pending' : (split.status?.toUpperCase() || 'ACTIVE'),
          createdAt: run.startTimestamp || new Date().toISOString(),
          amount: run.runningAmount || 0,  // 💰 actual backend total from API
        }))
      ) || [];

    // Determine table status - Priority: split status > main table status
    let tableStatus = table.status?.toLowerCase() || 'available';

    // ✅ Check if there are any active, held, OR payment_pending splits
    const hasActiveSplits = table.split?.some(split =>
      split.status === 'active' || split.status === 'hold' || split.status === 'HOLD' || split.status === 'payment_pending'
    );

    if (hasActiveSplits) {
      // If there are active/held/payment_pending splits, table is occupied
      tableStatus = 'occupied';
    } else {
      // Otherwise, use main table status
      tableStatus = table.status?.toLowerCase() || 'available';
    }

    // ✅ Calculate total guest count from all active, held, AND payment_pending splits
    const totalGuests = table.split
      ?.filter(split =>
        split.status === 'active' || split.status === 'hold' || split.status === 'HOLD' || split.status === 'payment_pending'
      )
      ?.reduce((sum, split) => sum + (split.peopleCount || 0), 0) || 0;

    return {
      id: table.id,
      name: table.tableNumber || `Table ${table.id}`,
      tableNumber: table.tableNumber,
      section: table.sectionName || `Section ${table.idSection}`,
      sectionId: table.idSection,
      capacity: table.seatingCapacity,
      seatingCapacity: table.seatingCapacity, // Keep for backward compatibility
      status: tableStatus, // Auto-calculated from running orders
      isActive: table.isActive ?? true,
      tableShape: table.tableShape,
      branchId: table.branchId || null,
      branchName: table.branchName || null,
      runningOrders, // ✅ Only active orders with amounts from API
      allSplits: table.split || [], // Keep all splits for reference
      totalGuests, // ✅ Sum of all active splits' peopleCount
      reservations: table.reservations || [], // ✅ Include reservations from API
    };
  });
};




  /**
   * Fetch all tables from API with fallback to hardcoded tables
   */
  const fetchTables = async () => {
    setLoading(true);
    setError(null);
    setUsingFallback(false);

    try {
      const response = await tableService.getAllTables();

      // Handle different response structures
      let rawTables = response?.data?.data || response?.data || response || [];

      // Process table data
      const processedTables = processTableData(rawTables);

      if (processedTables.length === 0) {
        throw new Error('No tables received from API');
      }

      setTables(processedTables);
      setLoading(false);

    } catch (err) {
      console.error('❌ Error fetching tables from API:', err);

      // Process fallback tables too
      const processedFallback = processTableData(FALLBACK_TABLES);
      setTables(processedFallback);
      
      setError(err.message || 'Failed to load tables from API');
      setUsingFallback(true);
      setLoading(false);

      toast('Using offline table data', {
        icon: '📡',
        duration: 3000,
      });
    }
  };

  /**
   * Refresh tables manually
   */
  const refreshTables = async () => {
    await fetchTables();
    if (!usingFallback) {
      toast.success('Tables refreshed from server', {
        duration: 2000,
      });
    }
  };

  /**
   * Fetch tables on component mount
   */
  useEffect(() => {
    fetchTables();
  }, []);

  return {
    tables,
    loading,
    error,
    usingFallback,
    refreshTables,
    fetchTables,
  };
};

export default useTables;